import { useHover } from '@passkeys/hooks';
import Text from '@passkeys/ui/text';
import { useEffect, useRef } from 'react';

import ChainMenu from '@/ui/components/chain-menu';
import { CHAIN_ITEM_HEIGHT } from '@/ui/components/chain-menu/constants';
import exodus from '@/ui/exodus.js';
import useMenuChains from '@/ui/hooks/use-menu-chains';
import useNavigate from '@/ui/hooks/use-navigate';
import { cn } from '@/ui/utils/classnames';
import Avatar from './parts/avatar.js';
import useModal from '../../hooks/use-modal.js';

const BUTTON_SIZES = {
  labelled: { height: 40, width: 128 },
  logo: { height: 40, width: 40 },
};

// The transitions need fixed values to work.
const EXPANDED_WIDTH = 208;
const EXPANDED_CLICKABLE_AREA_HEIGHT = 40;
const BUTTON_CHAIN_MENU_GAP = 8;

const HOVER_DELAY = 450;

type MyPasskeyButtonProps = {
  variant?: keyof typeof BUTTON_SIZES;
  className?: string;
};

function MyPasskeyButton({
  variant = 'labelled',
  className,
}: MyPasskeyButtonProps) {
  const ref = useRef<HTMLDivElement>(null);

  const isLogo = variant === 'logo';
  const collapsedSize = BUTTON_SIZES[variant];

  const modal = useModal();

  // Tailwind does not support dynamic arbitrary values so we are using the
  // `style` prop. When using `style`, we miss on `group-hover` and need to keep
  // track of hover state via JS.
  const hover = useHover(ref, { delay: HOVER_DELAY });

  const navigate = useNavigate();
  const handleClick = () => {
    navigate('/my-passkey');
  };

  const chains = useMenuChains();

  const hasChains = chains.length > 0;
  const expanded = hover && hasChains;

  const width = expanded ? EXPANDED_WIDTH : collapsedSize.width;

  const height = expanded
    ? Math.ceil(
        EXPANDED_CLICKABLE_AREA_HEIGHT +
          BUTTON_CHAIN_MENU_GAP +
          CHAIN_ITEM_HEIGHT * chains.length,
      )
    : collapsedSize.height;

  useEffect(() => {
    if (!hover) {
      return;
    }

    exodus.analytics.track({
      event: 'WalletAddressDropdownView',
    });
  }, [hover]);

  return (
    <div
      className={cn('relative z-50', className)}
      style={{
        width: `${collapsedSize.width}px`,
        height: `${collapsedSize.height}px`,
      }}
    >
      <div
        ref={ref}
        className={cn(
          'relative inset-0 isolate flex flex-none flex-col overflow-hidden *:z-[1]',
          'bg-gray-100 theme-dark:bg-neutral-700',
          'before:absolute before:inset-0 before:bg-gradient-to-b before:from-gray-200 before:to-gray-200 theme-dark:before:from-[#303033]/20 theme-dark:before:to-[#37373b]/60',
          'after:absolute after:inset-px after:bg-gray-100 theme-dark:after:bg-neutral-700',
          'transition-[height,width,border-radius,box-shadow] duration-300 ease-[cubic-bezier(0.25,0.1,0,1)]',
          'before:transition-[border-radius] before:duration-300 before:ease-[cubic-bezier(0.25,0.1,0,1)] after:transition-[border-radius] after:duration-300 after:ease-[cubic-bezier(0.25,0.1,0,1)]',
          expanded
            ? cn(
                'rounded-[16px] shadow-md',
                'delay-0',
                'before:rounded-[16px] after:rounded-[15px]',
                'before:delay-0 after:delay-0',
                isLogo &&
                  'rounded-tl-[20px] before:rounded-tl-[20px] after:rounded-tl-[19px]',
              )
            : cn(
                'rounded-[20px] shadow-none', // Not using 'rounded-full' because border-radius needs to be animated.
                'delay-150',
                'before:rounded-[20px] after:rounded-[19px]',
                'before:delay-150 after:delay-150',
              ),
        )}
        style={{ height: `${height}px`, width: `${width}px` }}
      >
        <button
          className="absolute left-0 top-0 flex flex-1 flex-row items-center gap-x-2 p-2"
          style={{
            width: `${width}px`,
            height: `${collapsedSize.height}px`,
          }}
          data-testid="my-passkey-button"
          onClick={handleClick}
          type="button"
          disabled={!!modal}
        >
          <Avatar
            size={isLogo ? 'large' : 'small'}
            className={cn(isLogo && '-ml-2')}
          />
          <Text size={14} className="text-nowrap">
            My Passkey
          </Text>
        </button>

        <div
          className={cn(
            'transition-opacity duration-150',
            expanded
              ? 'opacity-100 delay-150'
              : 'pointer-events-none opacity-0 delay-0',
          )}
          style={{
            marginTop: `${collapsedSize.height}px`,
          }}
        >
          <ChainMenu chains={chains} className="px-2 pb-3 pt-1" />
        </div>
      </div>
    </div>
  );
}

export default MyPasskeyButton;
